import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ChevronLeft, Sun, Moon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../model/useAuth';
import { useTheme } from '@/app/providers/ThemeProvider';
import { ZafiroLogo } from '@/shared/components/ZafiroLogo';
import { Button, Input, Alert } from '@/shared/components/ui';
import { MENSAJES_ERROR, TEXTOS_UI } from '../constants';
import './LoginForm.css';

export const LoginForm = () => {
  const [loginData, setLoginData] = useState({
    UsuarioId: '',
    Contrasena: ''
  });
  const [step, setStep] = useState(1); // 1 = usuario, 2 = contraseña
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTestUsers, setShowTestUsers] = useState(false);
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (loginData.UsuarioId.trim()) {
      setStep(2);
    } else {
      setError(MENSAJES_ERROR.USUARIO_REQUERIDO);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await login(loginData);
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message ||
        err.message ||
        MENSAJES_ERROR.ERROR_LOGIN;
      setError(errorMessage);
      console.error('[LoginForm] Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fillTestUser = (username: string) => {
    setLoginData({ UsuarioId: username, Contrasena: '123456' });
    setStep(2);
    setError('');
    setShowTestUsers(false);
  };

  const isDarkMode = theme === 'dark' || theme === 'sapphire';

  // Theme toggle button para rightAddon
  const themeToggleButton = (
    <button
      type="button"
      onClick={toggleTheme}
      className="theme-toggle-btn"
      title={isDarkMode ? TEXTOS_UI.TEMA_CAMBIAR_CLARO : TEXTOS_UI.TEMA_CAMBIAR_OSCURO}
      aria-label={isDarkMode ? TEXTOS_UI.TEMA_CAMBIAR_CLARO : TEXTOS_UI.TEMA_CAMBIAR_OSCURO}
    >
      {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  );

  return (
    <div className="login-container">
      <div className="login-content">
        {/* Logo fuera del formulario */}
        <div className="login-logo-section">
          <ZafiroLogo />
        </div>

        <div className="login-card">
          {/* Header */}
          <div className="login-header">
            <h1>{TEXTOS_UI.LOGIN_TITULO}</h1>
            <p>{TEXTOS_UI.LOGIN_SUBTITULO}</p>
          </div>

          <div className="login-form-content">
            {step === 1 && (
              <form onSubmit={handleContinue} className="login-form">
                <Input
                  id="UsuarioId"
                  name="UsuarioId"
                  type="text"
                  label={TEXTOS_UI.LOGIN_USUARIO_LABEL}
                  value={loginData.UsuarioId}
                  onChange={handleInputChange}
                  placeholder={TEXTOS_UI.LOGIN_USUARIO_PLACEHOLDER}
                  className="form-input"
                  required
                  disabled={isLoading}
                  autoFocus
                  rightAddon={themeToggleButton}
                  noMargin
                />

                {error && <Alert type="error">{error}</Alert>}

                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  disabled={isLoading}
                  loading={isLoading}
                  className="login-btn"
                >
                  {TEXTOS_UI.LOGIN_CONTINUAR_BOTON}
                </Button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleLogin} className="login-form">
                {/* Mostrar usuario de forma discreta */}
                <div className="user-display">
                  <p>Usuario: <span className="user-name">{loginData.UsuarioId}</span></p>
                </div>

                <div className="form-group-login">
                  <div className="password-label-row">
                    <label htmlFor="Contrasena" className="form-label">
                      {TEXTOS_UI.LOGIN_PASSWORD_LABEL}
                    </label>
                    <button
                      type="button"
                      className="forgot-password-link"
                      onClick={() => navigate('/recuperar-contrasena')}
                    >
                      {TEXTOS_UI.LOGIN_OLVIDO_PASSWORD}
                    </button>
                  </div>
                  <Input
                    id="Contrasena"
                    name="Contrasena"
                    type="password"
                    value={loginData.Contrasena}
                    onChange={handleInputChange}
                    placeholder={TEXTOS_UI.LOGIN_PASSWORD_PLACEHOLDER}
                    className="form-input"
                    required
                    disabled={isLoading}
                    autoFocus
                    showPasswordToggle
                    noMargin
                  />
                </div>

                {error && <Alert type="error">{error}</Alert>}

                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  disabled={isLoading}
                  loading={isLoading}
                  className="login-btn"
                >
                  {TEXTOS_UI.LOGIN_BOTON}
                </Button>

                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="back-btn"
                >
                  <ChevronLeft size={16} />
                  {TEXTOS_UI.LOGIN_REGRESAR_BOTON}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Sección de usuarios de prueba */}
        <div className="test-users-section">
          <button
            type="button"
            className="test-users-toggle"
            onClick={() => setShowTestUsers(prev => !prev)}
          >
            {showTestUsers ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {showTestUsers ? TEXTOS_UI.USUARIOS_PRUEBA_OCULTAR : TEXTOS_UI.USUARIOS_PRUEBA_MOSTRAR}
          </button>

          {showTestUsers && (
            <div className="test-users-grid">
              {[
                { username: '1000000000', role: 'Administrador' },
                { username: '1143337392', role: 'Desarrollador' }
              ].map(({ username, role }) => (
                <button
                  key={username}
                  onClick={() => fillTestUser(username)}
                  className="test-user-btn"
                >
                  <div className="test-user-role">{role}:</div>
                  <div className="test-user-credentials">{username} / 123456</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
